import type { Logger } from "@expert/logging";
import type { KyInstance } from "ky";

import type { SalesAnalytics } from "../sales-context/SalesContext.types";

import { useProductOfferStore } from "../stores/useProductOfferStore";
import { SaleProgress, SalesProvider } from "../types/types-sales";
import { buildEnrollmentOutcomeLog } from "./enrollment.helpers";
import { getProvider } from "./getProductOffer";

export const EnrollmentStatus = {
    Error: "ERROR",
    Success: "SUCCESS",
    Failure: "FAILURE",
    Declined: "DECLINED",
} as const;
export type EnrollmentStatus = (typeof EnrollmentStatus)[keyof typeof EnrollmentStatus];

/**
 * Map a backend enrollmentStatus to the store's SaleProgress (case-insensitive). SUCCESS and
 * DECLINED are both terminal: DECLINED means the offer already carries a permanent (fraud-avoidance)
 * declination, so the enrollment is refused and the offer flips terminal — NOT an enrollment error.
 * Returns undefined for unknown/unmapped statuses (e.g. the ERROR sentinel), which callers treat as
 * a non-terminal, retryable failure. Shared by the sync enroll path (createEnrollment) and the async
 * WebSocket path (handleEnrollmentEvent) so both attribute the same outcome identically. (The
 * product-offer polling path has its own SUCCESS/FAILURE gate and does not consume declines.)
 */
export const enrollmentStatusToSaleProgress = (status: string): SaleProgress | undefined => {
    switch (status?.toUpperCase()) {
        case "SUCCESS":
            return SaleProgress.Success;
        case "DECLINED":
            return SaleProgress.Declined;
        case "FAILURE":
            return SaleProgress.Failure;
    }
};

export interface Enrollment {
    sessionId: string;
    partner: string;
    productOfferId: string;
    productSku: string;
    confirmationCode?: string;
    salesProvider?: SalesProvider;
    [key: string]: unknown;
}

export interface EnrollmentResponse {
    enrollmentStatus: string;
    productSku: string;
    reason?: string;
}

export const createEnrollment = async (
    client: KyInstance,
    logger: Logger,
    analytics: SalesAnalytics,
    enrollment: Enrollment,
) => {
    const childLogger = logger.child({ module: "enrollment", productOfferId: enrollment.productOfferId });

    if (enrollment.salesProvider === SalesProvider.NOOP) {
        childLogger.warn("Attempted to enroll NOOP product; no-op");
        return { enrollmentStatus: EnrollmentStatus.Success, productSku: enrollment.productSku };
    }

    // Attribute analytics to the offer's provider (e.g. expert-sales for USP-routed Verizon traffic),
    // falling back to ASP for callers that don't set one. The business event uses the same
    // "ASP" / "EXPERTSALES" form as the product-offer events; Honeycomb and FullStory stay lowercase.
    const statusOrigin = enrollment.salesProvider ?? SalesProvider.ASP;

    analytics.startDynatraceAction("SaleEnrollment", enrollment.productOfferId);
    try {
        childLogger.info("Making enrollment request");
        const result = await client.post("v2/enrollment", { json: enrollment }).json<EnrollmentResponse>();
        childLogger.info("Finished enrollment request");

        childLogger.info(buildEnrollmentOutcomeLog(enrollment, result), "Enrollment outcome");

        void analytics.dispatcher.dispatchBusinessEvent("GetEnrollmentStatus", {
            ProductOfferId: enrollment.productOfferId,
            enrollmentStatus: result.enrollmentStatus,
            StatusOrigin: getProvider(statusOrigin),
            productSku: result.productSku,
            confirmationCode: enrollment.confirmationCode,
        });

        const { getProducts, getProductInfo } = useProductOfferStore.getState();
        const enrolledProducts = getProducts(enrollment.sessionId)
            .filter((p) => p.saleProgress === SaleProgress.Success)
            .map((p) => ({
                productSku: p.productSku,
                productOfferId: p.id,
                enrollmentStatus: p.saleProgress,
                statusOrigin: p.salesProvider,
                displayName: getProductInfo(enrollment.sessionId, p.productSku)?.displayName,
            }));
        const productInfo = getProductInfo(enrollment.sessionId, enrollment.productSku);
        const saleProgress = enrollmentStatusToSaleProgress(result.enrollmentStatus);
        const newEnrollment = {
            enrollmentStatus: saleProgress ?? SaleProgress.Failure,
            productSku: enrollment.productSku,
            productOfferId: enrollment.productOfferId,
            statusOrigin,
            displayName: productInfo?.displayName,
        };
        analytics.sendToHoneycomb(enrollment.sessionId, {
            enrollment: enrolledProducts.concat([newEnrollment]),
        });

        // A DECLINED offer is a terminal, expected outcome — NOT an error. Only a real failure is
        // attributed as a Dynatrace error / EnrollmentFailure so declines don't inflate error rates.
        const isSuccess = saleProgress === SaleProgress.Success;
        const isDeclined = saleProgress === SaleProgress.Declined;
        const eventName = isSuccess ? "EnrollmentSuccess" : isDeclined ? "EnrollmentDeclined" : "EnrollmentFailure";

        analytics.trackFullStoryEvent(eventName, {
            productSku: enrollment.productSku,
            productOfferId: enrollment.productOfferId,
            sessionId: enrollment.sessionId,
            partner: enrollment.partner,
            enrollmentStatus: result.enrollmentStatus,
            statusOrigin,
        });

        analytics.endDynatraceAction(
            "SaleEnrollment",
            isSuccess || isDeclined ? undefined : { taskId: enrollment.productOfferId, error: eventName },
        );

        return result;
    } catch (error: unknown) {
        void analytics.dispatcher.dispatchBusinessEvent("EnrollmentError", {
            Status: "LAMBDA_ERROR",
            ProductOfferId: enrollment.productOfferId,
            Error: error,
            productSku: enrollment.productSku,
        });

        childLogger.warn({ error, enrollment }, "enrollment api call failed");
        analytics.endDynatraceAction("SaleEnrollment", {
            taskId: enrollment.productOfferId,
            error: "EnrollmentError",
        });
        return { enrollmentStatus: EnrollmentStatus.Error };
    }
};
